// InnovateFlow interface redesign — Workspace context (Spec §5.1).
//
// The Workspace Switcher is the single place a user *manually* changes
// context; the sidebar re-renders to the active workspace's authorised nav
// (P6). This context derives the *available* workspaces from real identity
// data — org_memberships (owner/admin → Organisation), aggregator memberships
// (→ Aggregator), and the innovateflow-admin Cognito group (→ Platform Admin) —
// and persists the active selection per-user in localStorage.
//
// Direct-URL navigation (a bookmark, a shared link, a refresh landing on
// /org/*, /agg or /admin) is a SECOND way a workspace gets entered, and
// previously did nothing — the shell kept rendering whatever workspace was last
// active regardless of the URL. A route watcher below derives the workspace kind
// the current pathname belongs to (config/navigation.ts `workspaceKindForPath`)
// and switches into it the same way the switcher would, including scoping
// org/aggregator-scoped API calls to the right entity (`setActiveOrgId` /
// `setActiveAggregatorId`).
//
// UX-014: the watcher works in both directions. "/", "/settings", "/account/*"
// and "/support" belong to the Personal workspace, so an organisation admin who
// clicks the logo gets the personal sidebar, not the org one around a personal
// page. It only ever picks from `workspaces` — the list built from real
// memberships and groups — so a URL can never grant a workspace.

import { createContext, useContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { useAggregator } from './AggregatorContext'
import { isPersonalOrgMembership } from '../hooks/useActiveOrg'
import { resolveActiveWorkspaceForRoute, type WorkspaceKind } from '../config/navigation'

export interface Workspace {
  /** stable id: 'personal' | 'admin' | `org:${orgId}` | `agg:${aggId}` */
  id: string
  kind: WorkspaceKind
  /** display name in the switcher + top-bar chip */
  name: string
  /** contextual entity id (org_id / aggregator_id) where relevant */
  entityId?: string
  /** organisation-kind only: the caller's role in that org ('Owner' / 'Admin'), for the workspace pill */
  roleLabel?: string
}

/** Top-bar/sidebar pill text — an org workspace also shows the caller's role
 * (e.g. "Meridian Ventures — Admin"); other kinds show just their name, which
 * is already unambiguous ("Apex Accelerator", "Platform Admin"). */
export function workspacePillLabel(w: Workspace): string {
  if (w.kind === 'organisation' && w.roleLabel) return `${w.name} — ${w.roleLabel}`
  return w.name
}

interface WorkspaceContextType {
  workspaces: Workspace[]
  active: Workspace
  setActiveWorkspace: (id: string) => void
}

const PERSONAL: Workspace = { id: 'personal', kind: 'personal', name: 'Personal' }
const STORAGE_KEY = 'activeWorkspaceId'

// Every user gets an owner-level "organisation of one" (seat buying, inviting
// collaborators). It is a plan, not a real organisation, so it must not read as one (UX-042).
const PERSONAL_PLAN_LABEL = 'My plan'

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

const ORG_ADMIN_ROLES = new Set(['owner', 'admin'])

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// BUG found live-verifying this story: every user has an auto-created
// `org_type: 'personal'` membership (their own solo "org of one", used e.g. to
// buy seats and invite collaborators without ever joining a real
// organisation) whose `org_name` is seeded to the user's own email address —
// live dev data for a real test account confirmed this
// (org_id `personal-<uuid>`, org_name === the login email). The prior
// `m.org_name || 'Organisation'` fallback only covered a MISSING name, so this
// workspace's pill/breadcrumb rendered the raw email, violating "never a raw
// email or UUID". Generalised beyond the personal-org case so any other
// membership whose org_name happens to be email- or UUID-shaped (bad sync,
// not yet backfilled) degrades the same safe way instead of leaking it.
function safeWorkspaceName(rawName: string | undefined, fallback: string): string {
  const trimmed = rawName?.trim()
  if (!trimmed || EMAIL_RE.test(trimmed) || UUID_RE.test(trimmed)) return fallback
  return trimmed
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, setActiveOrgId } = useAuth()
  const { memberships: aggMemberships, setActiveAggregatorId } = useAggregator()
  const { pathname } = useLocation()

  const workspaces = useMemo<Workspace[]>(() => {
    const list: Workspace[] = [PERSONAL]
    // Organisation workspaces — one per org the user administers.
    for (const m of user?.org_memberships ?? []) {
      if (ORG_ADMIN_ROLES.has(m.role)) {
        const isPersonalOrg = isPersonalOrgMembership(m)
        list.push({
          id: `org:${m.org_id}`,
          kind: 'organisation',
          name: isPersonalOrg ? PERSONAL_PLAN_LABEL : safeWorkspaceName(m.org_name, 'Organisation'),
          entityId: m.org_id,
          roleLabel: m.role.charAt(0).toUpperCase() + m.role.slice(1),
        })
      }
    }
    // Aggregator workspaces — one per membership.
    for (const m of aggMemberships ?? []) {
      list.push({
        id: `agg:${m.aggregator_id}`,
        kind: 'aggregator',
        name: safeWorkspaceName(m.aggregator_name, 'Aggregator'),
        entityId: m.aggregator_id,
      })
    }
    // Platform Admin — innovateflow-admin group only.
    if ((user?.groups ?? []).includes('innovateflow-admin')) {
      list.push({ id: 'admin', kind: 'admin', name: 'Platform Admin' })
    }
    return list
  }, [user?.org_memberships, user?.groups, aggMemberships])

  const [activeId, setActiveId] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'personal'
    } catch {
      return 'personal'
    }
  })

  // If the persisted selection is no longer available (identity changed,
  // membership revoked), fall back to Personal — never strand the user in a
  // workspace they cannot see.
  const active = useMemo<Workspace>(
    () => workspaces.find((w) => w.id === activeId) ?? PERSONAL,
    [workspaces, activeId],
  )

  useEffect(() => {
    if (active.id !== activeId) setActiveId(active.id)
  }, [active.id, activeId])

  // Shared by the manual switcher AND the route watcher below, so direct-URL
  // navigation and an explicit click both scope org/aggregator queries the
  // same way — entering an aggregator workspace sets the active aggregator
  // (API interceptor), entering an organisation workspace sets active_org_id
  // (BUG: previously only the aggregator branch did this — an org admin with
  // more than one organisation who switched workspaces still had every
  // org-scoped query resolve against `org_memberships[0]`, not the org they
  // just selected).
  const applyWorkspaceSelection = useCallback(
    (next: Workspace) => {
      setActiveId(next.id)
      try {
        localStorage.setItem(STORAGE_KEY, next.id)
      } catch {
        /* storage unavailable — selection is still honoured in-session */
      }
      if (next.kind === 'aggregator' && next.entityId && setActiveAggregatorId) {
        setActiveAggregatorId(next.entityId)
      }
      if (next.kind === 'organisation' && next.entityId) {
        setActiveOrgId(next.entityId)
      }
    },
    [setActiveAggregatorId, setActiveOrgId],
  )

  const setActiveWorkspace = useCallback(
    (id: string) => {
      const next = workspaces.find((w) => w.id === id) ?? PERSONAL
      applyWorkspaceSelection(next)
    },
    [workspaces, applyWorkspaceSelection],
  )

  // Direct-URL workspace derivation (see file header): a route under
  // /organisation, /org, /agg, /team or /admin forces the matching workspace
  // active, and "/" or an account route forces Personal (UX-014), even when the
  // user never touched the switcher. No-ops on routes every workspace shows,
  // when already on the right kind, or when the user has no authorised
  // workspace of that kind (the page-level gate handles the "not authorised"
  // message).
  useEffect(() => {
    const target = resolveActiveWorkspaceForRoute(pathname, workspaces, active)
    if (target) applyWorkspaceSelection(target)
  }, [pathname, workspaces, active, applyWorkspaceSelection])

  const value = useMemo(
    () => ({ workspaces, active, setActiveWorkspace }),
    [workspaces, active, setActiveWorkspace],
  )

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace(): WorkspaceContextType {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider')
  return ctx
}
